import { makeAutoObservable } from 'mobx'
import type { LandingPageId } from '@/shared/config/site'
import { routePaths } from '@/shared/config/site'

export class LandingPageModel {
  public readonly id: LandingPageId

  public constructor(pageId: LandingPageId) {
    this.id = pageId

    makeAutoObservable(this, { content: false, path: false, proofItems: false }, { autoBind: true })
  }

  public get content() {
    const pageContent = [
      {
        boundaryTitle: 'Bad fit',
        closingBody:
          'Send the current state. I will tell you whether it needs a fix, a rebuild, or a dignified burial.',
        closingTitle: 'The demo got you here. Engineering gets you paid.',
        documentTitle: 'Vibecode App Help | Vitaliy',
        fitTitle: 'Good fit',
        id: 'vibecodes',
        intro:
          "You vibed your way to 80%, but now the code is a mess, the app keeps breaking, and prompting isn't working anymore. Let a real engineer untangle the spaghetti and get you to launch.",
        leadFormIntro: 'Two minutes beats another prompt spiral.',
        leadFormTitle: 'Send the messy version',
        navLabel: 'Vibecodes',
        primaryCta: 'Rescue My App',
        processTitle: 'How this usually works',
        proofTitle: 'What I fix',
        secondaryCta: 'See services',
        seoDescription:
          'Vibecode app help for non-technical founders who need AI-built demos turned into stable software, clear scope, and shippable product work.',
        title: 'AI built your prototype. An autist will build your product.',
        boundaryItems: [
          'You want someone to pretend the chatbot is your CTO.',
          'You need ten meetings before showing the broken thing.',
          'You are looking for cheap vibes instead of accountable delivery.',
        ],
        fitItems: [
          'You built with AI and got stuck near the finish line.',
          'You need a real engineer, not another prompt pack.',
          'You want blunt scope, working code, and less magical thinking.',
        ],
        processItems: [
          'You send the app, repo, screenshots, or the broken flow.',
          'I tell you what is real, what is fake, and what should be fixed first.',
          'I either fix the critical path or give you the plan another engineer can execute.',
        ],
        proofItems: [
          {
            description: 'It looks close enough to raise hope. I find the real gap between demo theater and product.',
            title: 'The 80 percent demo',
          },
          {
            description: 'I stop the app from changing shape every time one button, route, or checkout step moves.',
            title: 'The feature that breaks everything',
          },
          {
            description: 'I translate the code into plain English, then make the next build decision obvious.',
            title: 'The handoff nobody can explain',
          },
        ],
        visual: {
          alt: 'A fragmented app interface marked with broken connections and a red failure signal.',
          src: '/landing/vibecodes-evidence.webp',
        },
      },
      {
        boundaryTitle: 'Bad fit',
        closingBody: 'Send the failure path and the repo. I will help you stop negotiating with plausible nonsense.',
        closingTitle: 'Use AI for leverage. Use me for judgment.',
        documentTitle: 'Vibengineer Consulting | Vitaliy',
        fitTitle: 'Good fit',
        id: 'vibengineers',
        intro:
          'You know how to code, but the tech debt is piling up and the architecture is cracking. Bring in a hyper-focused senior engineer to solve the deep bugs, fix the architecture, and help you ship.',
        leadFormIntro: 'A sharp brief is enough to start.',
        leadFormTitle: 'Send the repo-shaped problem',
        navLabel: 'Vibengineers',
        primaryCta: 'Fix My Architecture',
        processTitle: 'The engagement',
        proofTitle: 'Where I help',
        secondaryCta: 'See services',
        seoDescription:
          'Engineering help for vibengineers using AI coding tools who need architecture review, implementation support, debugging, and production-ready delivery.',
        title: 'You got it off the ground. Now you need a Staff Engineer to keep it there.',
        boundaryItems: [
          'You want validation for every generated abstraction.',
          'You refuse to delete code the model sounded confident about.',
          'You need a cheerleader more than an engineer.',
        ],
        fitItems: [
          'You know enough to be dangerous and want sharper judgment.',
          'You need someone who can work inside the code, not around it.',
          'You care about shipping without pretending the mess is fine.',
        ],
        processItems: [
          'Fast repo read with the failure path front and center.',
          'A decision list: keep, cut, fix now, fix later.',
          'Focused implementation or pair-style review on the part that keeps slipping.',
        ],
        proofItems: [
          {
            description: 'I separate useful structure from AI cosplay before the app ossifies around bad decisions.',
            title: 'Architecture triage',
          },
          {
            description: 'I track the actual boundary instead of prompting around the same symptom for six hours.',
            title: 'Debugging loops',
          },
          {
            description:
              'I tighten routes, state, data flow, error handling, and deployment checks until the app behaves.',
            title: 'Production hardening',
          },
        ],
        visual: {
          alt: 'A dark terminal workspace with repo notes, highlighted diffs, and a green failure trace.',
          src: '/landing/vibengineers-terminal.webp',
        },
      },
      {
        boundaryTitle: 'Bad fit',
        closingBody: 'Send the hard part. If I am the wrong person, I will say that quickly.',
        closingTitle: 'I am not cheap. I am cheaper than the wrong fix.',
        documentTitle: 'Senior Engineering Help | Vitaliy',
        fitTitle: 'Good fit',
        id: 'engineers',
        intro:
          "I don't care about ping-pong tables. I care about deep technical problem-solving and shipping bulletproof code. Send me to your hiring manager.",
        leadFormIntro: 'Stack trace, repo notes, and constraints are welcome.',
        leadFormTitle: 'Send the technical problem',
        navLabel: 'Engineers',
        primaryCta: 'Hire an Autist',
        processTitle: 'How I slot in',
        proofTitle: 'The work',
        secondaryCta: 'Open resume',
        seoDescription:
          'Senior software engineering help for teams that need deep debugging, frontend architecture, SSR repair, AI systems, Rust, React, and staff-level execution.',
        title: 'The Staff Engineer your team is desperately missing.',
        boundaryItems: [
          'You need someone to disappear into process theater.',
          'You want architecture astronauts with no shipped code.',
          'You cannot tolerate hearing the obvious part out loud.',
        ],
        fitItems: [
          'You need staff-level individual contribution without ceremony.',
          'You have a technical problem with a real business consequence.',
          'You want direct communication and defensible tradeoffs.',
        ],
        processItems: [
          'Read the failing boundary before touching the obvious file.',
          'Make the smallest useful fix that changes the actual outcome.',
          'Leave behind sharper code, sharper tests, and sharper ownership.',
        ],
        proofItems: [
          {
            description:
              'React, TypeScript, SSR, accessibility, state cleanup, and route-level behavior that survives production.',
            title: 'Frontend systems',
          },
          {
            description: 'Rust, Node, data-heavy workflows, latency cuts, deployment seams, and boring reliability.',
            title: 'Backend and performance',
          },
          {
            description:
              'Agent runtimes, model routing, tool contracts, RAG, MCP, and evaluation loops that behave like software.',
            title: 'AI product engineering',
          },
        ],
        visual: {
          alt: 'A system boundary map with connected components and a marked failure point.',
          src: '/landing/engineers-boundary.webp',
        },
      },
      {
        boundaryTitle: 'Bad fit',
        closingBody: 'If the role is aligned, I will engage. If it is not, nobody needs a seven-email dance.',
        closingTitle: 'Send a real role and I will respond like a real person.',
        documentTitle: 'Recruiters | Vitaliy',
        fitTitle: 'Good fit',
        id: 'recruiters',
        intro:
          "Elite pedigree. Staff-level expertise. The exact technical chops your hiring managers are begging for. Drop your email below, and let's get your client a candidate they'll actually want to hire.",
        leadFormIntro: 'Relevant roles only. Salary range helps.',
        leadFormTitle: 'Send the role',
        navLabel: 'Recruiters',
        primaryCta: 'Send Me The Resume',
        processTitle: 'What to send',
        proofTitle: 'Why this profile is worth your email',
        secondaryCta: 'Open resume',
        seoDescription:
          'Recruiter landing page for Vitaliy, a senior software engineer with React, TypeScript, Rust, AI systems, frontend architecture, and technical leadership experience.',
        title: "The easiest placement you'll make this quarter.",
        boundaryItems: [
          'Unpaid exercises disguised as interviews.',
          'Mystery compensation.',
          'Roles where the job description is just buzzwords in a trench coat.',
        ],
        fitItems: [
          'Senior, staff, principal, founding engineer, or high-autonomy IC roles.',
          'Hard frontend, full-stack, AI systems, Rust, or technical leadership work.',
          'Teams that value directness more than performative consensus.',
        ],
        processItems: [
          'Role title, company, compensation range, and location expectations.',
          'Why the team needs this role now.',
          'Anything real about the technical problems behind the job post.',
        ],
        proofItems: [
          {
            description:
              'Nineteen years across product engineering, consulting, fintech, cloud, ecommerce, and AI-heavy systems.',
            title: 'Senior delivery',
          },
          {
            description: 'React, TypeScript, Rust, Node, React Native, SSR, data-heavy interfaces, and agent tooling.',
            title: 'Modern stack',
          },
          {
            description: 'I can lead, review, mentor, unblock, and still write the code when that is the useful move.',
            title: 'Leadership without fog',
          },
        ],
        visual: {
          alt: 'A clean candidate dossier with role signals, highlighted stack lines, and a red screening mark.',
          src: '/landing/recruiters-signal.webp',
        },
      },
    ].find((page) => page.id === this.id)

    if (!pageContent) {
      throw new Error(`Unknown landing page: ${this.id}`)
    }

    return pageContent
  }

  public get path(): string {
    return routePaths[this.id]
  }

  public get proofItems() {
    return this.content.proofItems
  }
}
